// @ts-nocheck
/**
 * ドラフトシステム - デッキ構築画面
 */

import React from 'react';
import { useDraftDeckBuilder } from '../hooks';
import { DRAFT_CONFIG } from '../constants';
import { draftStyles, getRarityStyle } from './styles/draftStyles';

/**
 * デッキ構築画面
 * @param {Object} props
 * @param {Object} props.session - ドラフトセッション
 * @param {Function} props.onAddToDeck - デッキ追加コールバック
 * @param {Function} props.onRemoveFromDeck - デッキから削除コールバック
 * @param {Function} props.onConfirm - 確定コールバック
 */
export const DraftDeckBuilder = ({
  session,
  onAddToDeck,
  onRemoveFromDeck,
  onConfirm,
}) => {
  const {
    sortedDeck,
    sortedSideboard,
    selectedCard,
    sortMode,
    filterType,
    timeRemaining,
    isConfirmed,
    validation,
    stats,
    costCurve,
    selectCard,
    clearSelection,
    addToDeck,
    removeFromDeck,
    confirmDeck,
    autoBuild,
    setSortMode,
    setFilterType,
    deckCount,
    sideboardCount,
    canConfirm,
    canAddMore,
  } = useDraftDeckBuilder(session, { onAddToDeck, onRemoveFromDeck, onConfirm });

  // タイマーをフォーマット
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // コスト曲線の最大値
  const maxCurveCount = Math.max(...costCurve.map(c => c.count), 1);

  return (
    <div style={draftStyles.deckBuilderContainer}>
      {/* ヘッダー */}
      <div style={draftStyles.deckBuilderHeader}>
        <div style={draftStyles.deckBuilderTitle}>
          デッキ構築
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* ソート */}
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={{
              padding: '8px 12px',
              background: '#2a2a4a',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#fff',
            }}
          >
            <option value="cost">コスト順</option>
            <option value="type">タイプ順</option>
            <option value="attribute">属性順</option>
            <option value="rarity">レアリティ順</option>
          </select>

          {/* フィルター */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '8px 12px',
              background: '#2a2a4a',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#fff',
            }}
          >
            <option value="all">すべて</option>
            <option value="monster">モンスター</option>
            <option value="magic">魔法</option>
            <option value="field">フィールド</option>
            <option value="phase">フェイズ</option>
          </select>

          {/* タイマー */}
          <div style={{
            ...draftStyles.timerContainer,
            color: timeRemaining < 60 ? '#ff6b6b' : '#64c864',
          }}>
            <span>⏱️</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
              {formatTime(timeRemaining)}
            </span>
          </div>
        </div>
      </div>

      {/* メインエリア */}
      <div style={draftStyles.deckBuilderMain}>
        {/* サイドボード */}
        <div style={draftStyles.sideboardSection}>
          <div style={draftStyles.sideboardTitle}>
            <span>サイドボード</span>
            <span>{sideboardCount}枚</span>
          </div>
          <div style={draftStyles.sideboardGrid}>
            {sortedSideboard.map((card, index) => (
              <div
                key={card.id + '_sb_' + index}
                onClick={() => canAddMore && addToDeck(card.id)}
                style={{
                  ...draftStyles.packCard,
                  cursor: canAddMore ? 'pointer' : 'not-allowed',
                  opacity: canAddMore ? 1 : 0.5,
                }}
              >
                <div style={draftStyles.cardName}>{card.name}</div>
                <div style={draftStyles.cardInfo}>
                  <span style={{ ...draftStyles.cardRarity, ...getRarityStyle(card.rarity) }}>
                    {card.rarity}
                  </span>
                </div>
                <div style={{ ...draftStyles.cardInfo, marginTop: '4px' }}>
                  💎{card.cost || 0} {card.attribute}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* デッキ */}
        <div style={draftStyles.deckSection}>
          <div style={draftStyles.deckTitle}>
            <span>デッキ</span>
            <span style={{
              color: deckCount >= DRAFT_CONFIG.MIN_DECK_SIZE ? '#64c864' : '#ff6b6b',
            }}>
              {deckCount} / {DRAFT_CONFIG.MIN_DECK_SIZE}〜{DRAFT_CONFIG.MAX_DECK_SIZE}枚
            </span>
          </div>
          <div style={draftStyles.deckGrid}>
            {sortedDeck.map((card, index) => (
              <div
                key={card.id + '_deck_' + index}
                onClick={() => removeFromDeck(card.id)}
                style={{
                  ...draftStyles.packCard,
                  cursor: 'pointer',
                }}
              >
                <div style={draftStyles.cardName}>{card.name}</div>
                <div style={draftStyles.cardInfo}>
                  <span style={{ ...draftStyles.cardRarity, ...getRarityStyle(card.rarity) }}>
                    {card.rarity}
                  </span>
                </div>
                <div style={{ ...draftStyles.cardInfo, marginTop: '4px' }}>
                  💎{card.cost || 0} {card.attribute}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 統計 */}
        <div style={draftStyles.statsSection}>
          <div style={draftStyles.statsTitle}>統計</div>

          {/* タイプ別 */}
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>モンスター</span>
            <span style={draftStyles.statValue}>{stats.byType.monster}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>魔法</span>
            <span style={draftStyles.statValue}>{stats.byType.magic}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>フィールド</span>
            <span style={draftStyles.statValue}>{stats.byType.field}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>フェイズ</span>
            <span style={draftStyles.statValue}>{stats.byType.phase}</span>
          </div>

          <div style={{ borderTop: '1px solid #333', margin: '15px 0' }} />

          {/* コスト別 */}
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>低コスト(1-2)</span>
            <span style={draftStyles.statValue}>{stats.byCost.low}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>中コスト(3-4)</span>
            <span style={draftStyles.statValue}>{stats.byCost.mid}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>高コスト(5+)</span>
            <span style={draftStyles.statValue}>{stats.byCost.high}</span>
          </div>
          <div style={draftStyles.statItem}>
            <span style={draftStyles.statLabel}>平均コスト</span>
            <span style={draftStyles.statValue}>{stats.averageCost}</span>
          </div>

          {/* コスト曲線 */}
          <div style={draftStyles.costCurveContainer}>
            <div style={draftStyles.costCurveTitle}>コスト曲線</div>
            <div style={draftStyles.costCurveBar}>
              {costCurve.map((item, index) => (
                <div
                  key={index}
                  style={{
                    ...draftStyles.costCurveBarItem,
                    height: `${(item.count / maxCurveCount) * 100}%`,
                  }}
                  title={`${item.cost}: ${item.count}枚`}
                />
              ))}
            </div>
            <div style={draftStyles.costCurveLabels}>
              {costCurve.map((item, index) => (
                <div key={index} style={draftStyles.costCurveLabel}>
                  {item.cost}
                </div>
              ))}
            </div>
          </div>

          {/* バリデーション */}
          <div style={{
            ...draftStyles.validationSection,
            ...(validation.isValid ? draftStyles.validationValid : draftStyles.validationInvalid),
          }}>
            <div style={{
              ...draftStyles.validationTitle,
              color: validation.isValid ? '#64c864' : '#ff6b6b',
            }}>
              {validation.isValid ? '✓ デッキ有効' : '✗ デッキ無効'}
            </div>
            {validation.errors.map((error, index) => (
              <div key={index} style={{ ...draftStyles.validationMessage, color: '#ff6b6b' }}>
                • {error}
              </div>
            ))}
            {validation.warnings.map((warning, index) => (
              <div key={index} style={{ ...draftStyles.validationMessage, color: '#ffa500' }}>
                ⚠ {warning}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* アクションエリア */}
      <div style={draftStyles.deckBuilderActions}>
        <button
          onClick={autoBuild}
          disabled={isConfirmed}
          style={{
            ...draftStyles.autoButton,
            ...(isConfirmed ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          }}
        >
          自動構築
        </button>

        <button
          onClick={confirmDeck}
          disabled={!canConfirm}
          style={{
            ...draftStyles.confirmButton,
            ...(!canConfirm ? draftStyles.confirmButtonDisabled : {}),
          }}
        >
          {isConfirmed ? '確定済み' : 'デッキ確定'}
        </button>
      </div>
    </div>
  );
};

export default DraftDeckBuilder;
